import { InjectionToken } from '@angular/core';
import { IProductDataV3, Product } from '@df/catalog/product/product';
import { ProductConfiguration } from './model/product-configuration';
import { ProductConfigurationConfigurable } from './model/configurable/product-configuration-configurable';

export namespace NProductConfiguration {
    export const OPTIONS = new InjectionToken<IModuleOptions>('DF_MODULE_PRODUCT_CONFIGURATION_OPTIONS');
    export const Configuration = new InjectionToken<ProductConfiguration>('DF_MODULE_PRODUCT_CONFIGURATION_INSTANCE');

    export interface IModuleOptions {
        /**
         * Default configuration for configurable settings
         */
        configurable: Configurable.Config;
        bundle: Bundle.Config;
        grouped: Grouped.Config;
    }

    export namespace Bundle {
        export interface Config {
            /**
             * Storage key for hash storage
             */
            storageKey?: string | false;
            storage?: 'hash' | 'search' | false;
        }
    }

    export namespace Grouped {
        export interface Config {
            showOutOfStock?: boolean;
            sortBy?: 'position' | 'name' | 'price';
            minSelection?: number;
        }
    }

    export namespace Configurable {
        export type TRuleLogic = 'include' | 'exclude' | 'error';
        export type EngineResult = 'include' | 'exclude' | string | undefined;
        export type Engine = (attributeCode: string, value: number) => EngineResult;
        export type EngineGenerator<TEngineConfig = any> = (engineConfig: TEngineConfig) => Engine;
        // export type Matcher = (
        //     selection: Selection,
        //     product: Product<IProductDataV3>,
        //     priorityAttributes: string[]
        // ) => Product<IProductDataV3> | undefined;
        // export type MatcherGenerator = () => Matcher;

        export interface Selection {
            [attributeCode: string]: number | undefined;
        }

        export type THashStorageLogic = 'value' | 'label';

        export interface Config {
            /**
             * Storage key for hash storage
             */
            hashStorageKey?: string | false;
            hashStorageLogic: THashStorageLogic;
            noHashOnInit: boolean;
            /**
             * Sorted order of attribute codes
             */
            order: string[];
            /**
             * Engines for options display logic
             */
            optionsEngines: NProductConfiguration.Configurable.Engine[];
            /**
             * On selection logic
             */
            selection: SelectionConfig;
            /**
             * Meta state model
             */
            metaModel: 'parent' | 'child' | 'none';
            /**
             * URL model
             */
            urlModel: 'parent' | 'child' | 'none';
            /**
             * Default child selection
             */
            chooseChild: (
                matchResult: NProductConfiguration.Configurable.ChildrenMatchResult,
                selection: Selection
            ) => Product<IProductDataV3> | undefined;
            /**
             * Sanitize selection callback
             */
            sanitizeSelection?: (
                selection: Selection,
                product: Product<IProductDataV3>,
                productConfiguration: ProductConfigurationConfigurable
            ) => void;
        }

        export interface SelectionLogic {
            /**
             * Logic to proceeed when values cannot match products:
             * This is when we have selection that is conflicted internaly
             *
             * - unselect - unselect options (set undefiend)
             * - change - change selection to match simple
             * - prevent - prevent option selection
             */
            onMismatch: 'unselect' | 'change' | 'prevent' | 'ignore';
            /**
             * Logic to proceeed when selection is partial (or empty)
             *
             * - parent - leave configurable as current product
             * - simple - select simple product
             * - ignore - ignore (needed for basket area)
             */
            onPartial: 'parent' | 'simple' | 'ignore';
        }

        export interface SelectionConfig {
            default: SelectionLogic;
            parentChange?: Partial<SelectionLogic>;
            selectionChange?: Partial<SelectionLogic>;
        }

        export interface ChildrenMatchResult {
            children: Product<IProductDataV3>[];
            omittedAttributes: string[];
            undefinedAttributes: string[];
        }
    }

    export namespace Error {
        export enum Code {
            missing,
            noSelection,
            outOfStock,
            invalid
        }

        export enum Level {
            warning,
            error
        }

        export type Entry = EntryConfigurableAttribute | EntrySimple;

        export interface EntrySimple {
            code: Code.outOfStock | Code.noSelection;
            level: Level.error;
        }

        export interface EntryConfigurableAttribute {
            code: Code.missing | Code.invalid;
            level: Level.error;
            fieldName: string;
        }
    }

    export interface IPlugin {
        init: (productConfiguration: ProductConfiguration) => void;
    }
}
